/**
 * TypeScript mirror of ers_schema_v1.0.json.
 *
 * Keep this file in lock-step with the JSON. The Zod schema in ./zod.ts
 * validates the JSON at load-time so drift is caught early.
 *
 * Fields relaxed to tolerate real-world variability in the source JSON:
 *   - `notes_for_clarification.general` can be null for items awaiting QA.
 *   - `asterisk_note` may be boolean, null, or a string (footnote text).
 *   - `na_condition` / `observe_instruction` may be missing on some indicators.
 *   - `total_indicators` is null for the stubbed ECERS-3 instrument.
 */

export type IndicatorResponse = 'Y' | 'N' | 'NA';
export type ScoringLevel = 1 | 3 | 5 | 7;

export interface SchemaMetadata {
  description: string;
  schema_version: string;
  generated_date: string;
  author: string;
  instruments_included: string[];
  population_status: string;
  notes: string[];
  last_updated: string;
}

export interface ScoringRule {
  score: number;
  label: string | null;
  condition: string;
}

export interface ScoringAlgorithm {
  description: string;
  rules: ScoringRule[];
  na_rules: string[];
  subscale_score: string;
  total_scale_score: string;
}

export interface RatingScaleLevel {
  score: number;
  label: string | null;
  color_hex: string;
}

export interface Indicator {
  id: string;
  level: ScoringLevel;
  sequence: number;
  code: string; // e.g. "1.2", "5.4"
  text: string;
  example_text: string | null;
  allows_na: boolean;
  na_condition: string | null;
  observe_instruction: string | null;
  /**
   * Either a boolean flag OR a string footnote. Normalize at the call site via
   * `hasAsterisk(indicator)` / `asteriskNote(indicator)`.
   */
  asterisk_note: boolean | string | null;
}

export interface IndicatorClarificationNote {
  applies_to_codes: string[];
  note: string;
}

export interface NotesForClarification {
  /** null when the item's NFC is still awaiting QA (e.g. Item 10, page 34). */
  general: string | null;
  indicator_notes: IndicatorClarificationNote[];
}

export interface Item {
  id: string;
  instrument_id: string;
  subscale_id: string;
  number: number;
  name: string;
  page_reference: number | null;
  item_allows_na: boolean;
  /**
   * The rubric's own condition for scoring the whole item NA, shown in the
   * banner. Only meaningful when `item_allows_na` is true.
   */
  item_na_condition?: string | null;
  notes_for_clarification: NotesForClarification;
  indicators: Indicator[];
}

export interface Subscale {
  id: string;
  instrument_id: string;
  number: number;
  name: string;
  items: Item[];
}

export interface Instrument {
  id: string;
  full_name: string;
  short_name: string;
  edition: number;
  year_published: number;
  setting: string;
  age_range_description: string;
  age_range_months: { min: number; max: number };
  observation_duration_hours: number;
  total_subscales: number;
  total_items: number;
  total_indicators: number | null;
  publisher: string;
  authors: string[];
  indicator_response_options: IndicatorResponse[];
  /**
   * "Explanation of Terms Used Throughout the Scale" — the manual's glossary
   * (pp. 12-14). 19 Notes for Clarification cross-reference it by name, so it
   * ships with the instrument rather than living in a separate help page.
   */
  explanation_of_terms?: ExplanationOfTerms;
  /**
   * "Administration of the Scale" / "Scoring System" (manual pp. 9-11) — the
   * observation protocol, Observer Guidelines and the seven rating rules, as
   * published. Shown in the in-app help so observers are not relying on a
   * paraphrase of the protocol.
   */
  administration?: GuidanceSection[];
  subscales: Subscale[];
}

export interface GlossaryTerm {
  term: string;
  definition: string;
}

export interface ExplanationOfTerms {
  title: string;
  source_pages: number[];
  terms: GlossaryTerm[];
}

export interface GuidanceBlock {
  kind: 'numbered' | 'bullets' | 'para' | 'subheading';
  number: number | null;
  text: string;
  items?: string[];
}

export interface GuidanceSection {
  heading: string;
  source_pages: number[];
  blocks: GuidanceBlock[];
}

export interface ErsSchema {
  _metadata: SchemaMetadata;
  scoring_algorithm: ScoringAlgorithm;
  rating_scale_levels: RatingScaleLevel[];
  instruments: Instrument[];
}

/** True if the indicator has any kind of asterisk (flag or footnote text). */
export function hasAsterisk(indicator: Indicator): boolean {
  const a = indicator.asterisk_note;
  return a === true || (typeof a === 'string' && a.trim().length > 0);
}

/** The asterisk's footnote text if it's a string, otherwise null. */
export function asteriskNote(indicator: Indicator): string | null {
  return typeof indicator.asterisk_note === 'string'
    ? indicator.asterisk_note
    : null;
}
